/**
 * Stripe allowed tax id types (R5).
 *
 * Source of truth: https://docs.stripe.com/api/tax_ids/object (tax_ids.type enum).
 *
 * Keep this list in sync with Stripe's documented enum. Ordered roughly by
 * region/popularity to make the default select experience predictable.
 */

export interface StripeTaxIdType {
  /** Enum value to send to Stripe / the backend. */
  value: string;
  /** Two-letter ISO country this tax id type is associated with (informational). */
  country: string;
  /** Human-readable label. */
  label: string;
  /** Example id format — shown as placeholder/helper text. */
  example?: string;
}

export const STRIPE_TAX_ID_TYPES: readonly StripeTaxIdType[] = [
  // North America
  { value: "us_ein", country: "US", label: "US — EIN", example: "12-3456789" },
  { value: "ca_bn", country: "CA", label: "Canada — BN", example: "123456789" },
  {
    value: "ca_gst_hst",
    country: "CA",
    label: "Canada — GST/HST",
    example: "123456789RT0002",
  },
  {
    value: "ca_pst_bc",
    country: "CA",
    label: "Canada — PST BC",
    example: "PST-1234-5678",
  },
  {
    value: "ca_pst_mb",
    country: "CA",
    label: "Canada — PST MB",
    example: "123456-7",
  },
  {
    value: "ca_pst_sk",
    country: "CA",
    label: "Canada — PST SK",
    example: "1234567",
  },
  {
    value: "ca_qst",
    country: "CA",
    label: "Canada — QST",
    example: "1234567890TQ1234",
  },

  // Europe — EU VAT (+UK/CH/NO)
  { value: "eu_vat", country: "EU", label: "EU VAT", example: "DE123456789" },
  {
    value: "eu_oss_vat",
    country: "EU",
    label: "EU OSS VAT",
    example: "EU123456789",
  },
  {
    value: "gb_vat",
    country: "GB",
    label: "United Kingdom — VAT",
    example: "GB123456789",
  },
  {
    value: "ch_vat",
    country: "CH",
    label: "Switzerland — VAT",
    example: "CHE-123.456.789 MWST",
  },
  {
    value: "no_vat",
    country: "NO",
    label: "Norway — VAT",
    example: "123456789MVA",
  },
  {
    value: "no_voec",
    country: "NO",
    label: "Norway — VOEC",
    example: "1234567",
  },

  // Asia / Pacific
  {
    value: "au_abn",
    country: "AU",
    label: "Australia — ABN",
    example: "12 345 678 901",
  },
  {
    value: "au_arn",
    country: "AU",
    label: "Australia — ARN",
    example: "123456789012",
  },
  {
    value: "nz_gst",
    country: "NZ",
    label: "New Zealand — GST",
    example: "123-456-789",
  },
  {
    value: "sg_gst",
    country: "SG",
    label: "Singapore — GST",
    example: "M12345678X",
  },
  {
    value: "sg_uen",
    country: "SG",
    label: "Singapore — UEN",
    example: "123456789F",
  },
  {
    value: "hk_br",
    country: "HK",
    label: "Hong Kong — BR",
    example: "12345678",
  },
  {
    value: "my_itn",
    country: "MY",
    label: "Malaysia — ITN",
    example: "C 1234567890",
  },
  {
    value: "my_sst",
    country: "MY",
    label: "Malaysia — SST",
    example: "A10-1234-56789012",
  },
  {
    value: "my_frp",
    country: "MY",
    label: "Malaysia — FRP",
    example: "12345678",
  },
  {
    value: "th_vat",
    country: "TH",
    label: "Thailand — VAT",
    example: "1234567891234",
  },
  {
    value: "id_npwp",
    country: "ID",
    label: "Indonesia — NPWP",
    example: "12.345.678.9-012.345",
  },
  {
    value: "ph_tin",
    country: "PH",
    label: "Philippines — TIN",
    example: "123-456-789-000",
  },
  {
    value: "vn_tin",
    country: "VN",
    label: "Vietnam — TIN",
    example: "1234567890",
  },
  {
    value: "jp_cn",
    country: "JP",
    label: "Japan — Corporate Number",
    example: "1234567891234",
  },
  {
    value: "jp_rn",
    country: "JP",
    label: "Japan — Registered Foreign Businesses",
    example: "12345",
  },
  {
    value: "jp_trn",
    country: "JP",
    label: "Japan — Tax Registration Number",
    example: "T1234567891234",
  },
  {
    value: "kr_brn",
    country: "KR",
    label: "Korea — BRN",
    example: "123-45-67890",
  },
  {
    value: "tw_vat",
    country: "TW",
    label: "Taiwan — VAT",
    example: "12345678",
  },
  {
    value: "in_gst",
    country: "IN",
    label: "India — GST",
    example: "12ABCDE3456FGZH",
  },

  // Middle East
  {
    value: "ae_trn",
    country: "AE",
    label: "UAE — TRN",
    example: "123456789012345",
  },
  {
    value: "sa_vat",
    country: "SA",
    label: "Saudi Arabia — VAT",
    example: "123456789012345",
  },
  {
    value: "il_vat",
    country: "IL",
    label: "Israel — VAT",
    example: "000012345",
  },
  {
    value: "tr_tin",
    country: "TR",
    label: "Turkey — TIN",
    example: "0123456789",
  },
  {
    value: "eg_tin",
    country: "EG",
    label: "Egypt — TIN",
    example: "123456789",
  },

  // Africa
  {
    value: "za_vat",
    country: "ZA",
    label: "South Africa — VAT",
    example: "4123456789",
  },
  {
    value: "ke_pin",
    country: "KE",
    label: "Kenya — PIN",
    example: "P000111111A",
  },
  {
    value: "ng_tin",
    country: "NG",
    label: "Nigeria — TIN",
    example: "12345678-0001",
  },
  {
    value: "ma_vat",
    country: "MA",
    label: "Morocco — VAT",
    example: "12345678",
  },

  // LATAM
  {
    value: "mx_rfc",
    country: "MX",
    label: "Mexico — RFC",
    example: "ABC010203AB9",
  },
  {
    value: "br_cnpj",
    country: "BR",
    label: "Brazil — CNPJ",
    example: "01.234.456/5432-10",
  },
  {
    value: "br_cpf",
    country: "BR",
    label: "Brazil — CPF",
    example: "123.456.789-87",
  },
  {
    value: "cl_tin",
    country: "CL",
    label: "Chile — TIN",
    example: "12.345.678-K",
  },
  {
    value: "ar_cuit",
    country: "AR",
    label: "Argentina — CUIT",
    example: "12-3456789-0",
  },
  {
    value: "co_nit",
    country: "CO",
    label: "Colombia — NIT",
    example: "123.456.789-0",
  },
  {
    value: "pe_ruc",
    country: "PE",
    label: "Peru — RUC",
    example: "12345678901",
  },
  {
    value: "uy_ruc",
    country: "UY",
    label: "Uruguay — RUC",
    example: "123456789012",
  },
  {
    value: "ec_ruc",
    country: "EC",
    label: "Ecuador — RUC",
    example: "1234567890001",
  },
  {
    value: "bo_tin",
    country: "BO",
    label: "Bolivia — TIN",
    example: "1234567",
  },
  {
    value: "cr_tin",
    country: "CR",
    label: "Costa Rica — TIN",
    example: "1-234-567890",
  },
  {
    value: "do_rcn",
    country: "DO",
    label: "Dominican Republic — RCN",
    example: "123-4567890-1",
  },
  {
    value: "sv_nit",
    country: "SV",
    label: "El Salvador — NIT",
    example: "1234-567890-123-4",
  },
  {
    value: "gt_tin",
    country: "GT",
    label: "Guatemala — TIN",
    example: "1234567-8",
  },
  {
    value: "hn_rtn",
    country: "HN",
    label: "Honduras — RTN",
    example: "12345678901234",
  },
  {
    value: "ni_ruc",
    country: "NI",
    label: "Nicaragua — RUC",
    example: "1234567-8901234",
  },
  {
    value: "pa_ruc",
    country: "PA",
    label: "Panama — RUC",
    example: "12345-67-890123",
  },
  {
    value: "py_ruc",
    country: "PY",
    label: "Paraguay — RUC",
    example: "12345678-9",
  },
  {
    value: "ve_rif",
    country: "VE",
    label: "Venezuela — RIF",
    example: "V-12345678-9",
  },

  // Other
  {
    value: "ua_vat",
    country: "UA",
    label: "Ukraine — VAT",
    example: "123456789",
  },
  {
    value: "rs_pib",
    country: "RS",
    label: "Serbia — PIB",
    example: "123456789",
  },
  {
    value: "ge_vat",
    country: "GE",
    label: "Georgia — VAT",
    example: "123456789",
  },
  {
    value: "am_tin",
    country: "AM",
    label: "Armenia — TIN",
    example: "12345678",
  },
  {
    value: "by_tin",
    country: "BY",
    label: "Belarus — TIN",
    example: "123456789",
  },
  {
    value: "kz_bin",
    country: "KZ",
    label: "Kazakhstan — BIN",
    example: "123456789012",
  },
  {
    value: "uz_tin",
    country: "UZ",
    label: "Uzbekistan — TIN",
    example: "123456789",
  },
  {
    value: "uz_vat",
    country: "UZ",
    label: "Uzbekistan — VAT",
    example: "123456789012",
  },
];

/** Set of allowed Stripe tax id type values (for O(1) Zod validation). */
export const STRIPE_TAX_ID_TYPE_VALUES: ReadonlySet<string> = new Set(
  STRIPE_TAX_ID_TYPES.map((t) => t.value),
);

/** Lookup a tax id type by value. */
export function getStripeTaxIdType(value: string): StripeTaxIdType | undefined {
  return STRIPE_TAX_ID_TYPES.find((t) => t.value === value);
}
